/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from '../../registry/ComponentRegistry';
import { pageAtom } from 'designer/atoms';
import { genId } from 'designer/state';
import stringify from 'json-stable-stringify';
import {
  AnalyzeContext,
  AnalyzeIssue,
  AnalyzeResult,
} from 'layout/Sidebar/panels/TreeView/AnalyzeUtils';
import { camelCase } from 'lodash-es';
import { getNextAlias } from '../DataStore/DataStoreManager';
import { Item } from '../../registry/Component.type';
import { Platform } from '../../platform/Platform';
import { getManager } from '../../registry/Manager';

type SelectItem =
  | Item<'AsyncSelect'>
  | Item<'AsyncSelectColumn'>
  | Item<'Select'>
  | Item<'SelectColumn'>
  | Item<'SelectSearchField'>
  | Item<'SmartSearchAttribute'>;

function collectAttributes(
  platform: Platform,
  appUid: string,
  pageId: string,
  item: SelectItem,
): string {
  if (item.type === 'SmartSearchAttribute') {
    const { viewAttrCode, displayAttr } = item.props;
    const attList: string[] = [viewAttrCode ?? ''];
    if (displayAttr !== viewAttrCode) {
      attList.push(displayAttr ?? '');
    }
    return stringify(attList);
  }
  const { labelAttribute, valueAttribute } = item.props;
  const attList: string[] = [valueAttribute ?? ''];
  if (labelAttribute !== valueAttribute) {
    attList.push(labelAttribute ?? '');
  }
  const mappings = platform.getChildrenOfTypeById(
    appUid,
    pageId,
    item.itemId,
    'SelectReturnValueMapping',
  );
  mappings.forEach((mapping) => {
    if (
      mapping.props.sourceAttribute &&
      mapping.props.targetAttribute &&
      !attList.includes(mapping.props.sourceAttribute)
    ) {
      attList.push(mapping.props.sourceAttribute);
    }
  });
  return stringify(attList);
}

export async function analyzeSelect({
  platform,
  appUid,
  pageId,
  itemId,
  mgr,
}: AnalyzeContext): Promise<AnalyzeResult> {
  const alerts: AnalyzeIssue[] = [];
  const autoFixes: (() => void)[] = [];
  const page = platform.getPage(appUid, pageId);
  if (!page) {
    return null;
  }
  const item = page[itemId] as Item<'Select'>;
  const {
    optionValues,
    labelAttribute,
    valueAttribute,
    comboDatasourceAlias: alias,
  } = item.props;

  if (!alias) {
    if (!optionValues) {
      alerts.push({
        key: `incomplete-${itemId}`,
        showItemId: itemId,
        alert: `Incomplete ${mgr.name}. Option Values not setup!`,
        itemId,
      });
      return [alerts, autoFixes];
    }
    return null;
  }
  if (!(valueAttribute && labelAttribute)) {
    alerts.push({
      key: `incomplete-${itemId}`,
      showItemId: itemId,
      alert: `Incomplete ${mgr.name}. Missing value/label attribute!`,
      itemId,
    });
    return [alerts, autoFixes];
  }
  const itemIds = Object.keys(page);

  const dsItem = itemIds
    .filter(
      (id) =>
        page[id]?.type === 'DataStore' ||
        page[id]?.type === 'ReadOnlyDataStore' ||
        page[id]?.type === 'LocalDataStore' ||
        page[id]?.type === 'TransientDataStore',
    )
    .map((id) => page[id] as Item<'ReadOnlyDataStore'>)
    .find((ds) => ds.props.alias === alias);

  if (!dsItem) {
    alerts.push({
      key: `missing-alias-${itemId}`,
      showItemId: itemId,
      alert: `Missing data store for alias ${alias}!`,
      itemId,
    });
    return [alerts, autoFixes];
  }

  let hasMajorIssue = false;
  const attrs = collectAttributes(platform, appUid, pageId, item);

  const duplicateToReadonlyFix = () => {
    const _page = { ...platform.getPage(appUid, pageId) };
    const dataSource = platform.getDataSourceName(appUid, pageId, alias);
    if (!dataSource) {
      platform.showError(`Missing data source for alias ${alias}!`);
      return;
    }

    const map = platform.getDataSources(appUid, pageId);
    const newAlias = getNextAlias(`${camelCase(dataSource)}SelectAlias`, map);

    const ds: Item<'ReadOnlyDataStore'> = {
      children: [],
      type: 'ReadOnlyDataStore',
      parent: 'root',
      props: {
        dataSource,
        alias: newAlias,
        autoQuery: false,
        maxRows: 2000,
      },
      itemId: genId(_page),
    };
    const oldParent = _page.root;
    _page.root = {
      ...oldParent,
      children: [...oldParent.children, ds.itemId],
    };
    _page[ds.itemId] = ds;
    const updatedSelect: Item<'Select'> = {
      ...item,
      props: { ...item.props, comboDatasourceAlias: newAlias },
    };
    _page[itemId] = updatedSelect;
    getManager(oldParent.type).onChildMoveIn({
      appUid,
      pageId,
      page: _page,
      oldValue: oldParent,
      value: _page.root,
      child: ds,
    });
    platform.setter(pageAtom, _page, {
      params: { appUid, pageId },
    });
  };

  for (let i = 0; i < itemIds.length; i++) {
    const id = itemIds[i];
    // eslint-disable-next-line no-continue
    if (id === itemId) continue;
    const itm = page[id];
    const _mgr = getManager(itm.type);
    if (_mgr.isDataSourceAliasUsed(itm, alias)) {
      if (
        (
          [
            'Select',
            'AsyncSelect',
            'SelectColumn',
            'AsyncSelectColumn',
            'SelectSearchField',
            'SmartSearchAttribute',
          ] as ItemType[]
        ).includes(itm.type)
      ) {
        const _attrs = collectAttributes(
          platform,
          appUid,
          pageId,
          itm as SelectItem,
        );
        if (attrs !== _attrs) {
          alerts.push({
            key: `reuse-attrs-${itemId}`,
            fix: duplicateToReadonlyFix,
            showItemId: itemId,
            alert: `Data Store [${alias}] used by a ${mgr.name} is also used by ${_mgr.name} with different attribute combinations! `,
            tip: `Multiple Select can share the same Data Store as long as the combination of attributes used (i.e. value, display & mapping) are the same.

${attrs} != ${_attrs}

> Click \`Fix It\` to create a new Readonly Data Store for this ${mgr.name}.`,
            itemId,
          });
          break;
        }
      } else if (_mgr.managerType === 'Component') {
        hasMajorIssue = true;

        alerts.push({
          key: `reuse-${itemId}`,
          fix: duplicateToReadonlyFix,
          showItemId: itemId,
          alert: `Data Store [${alias}] used by a ${mgr.name} is also used by ${_mgr.name}! `,
          tip: `Select component must not be shared by non Select components.

Multiple Select can share the same Data Store as long as the combination 
of attributes used (i.e. value, display & mapping) are the same.

> Click \`Fix It\` to create a new Readonly Data Store for this ${mgr.name}.`,
          itemId,
        });
        break;
      }
    }
  }

  if (!hasMajorIssue && dsItem.props.autoQuery) {
    const fix = () => {
      const _page = { ...platform.getPage(appUid, pageId) };
      const udpatedDs: Item<'ReadOnlyDataStore'> = {
        ...dsItem,
        props: { ...dsItem.props, autoQuery: false },
      };
      _page[dsItem.itemId] = udpatedDs;
      platform.setter(pageAtom, _page, {
        params: { appUid, pageId },
      });
    };
    autoFixes.push(fix);
    alerts.push({
      key: `auto-${itemId}`,
      fix,
      showItemId: dsItem.itemId,
      alert: `Data Store [${alias}] used by a select has auto query on!`,
      tip: `Select component controls the data store query, so turn off the auto query.

 > Click \`Fix It\` to turn off the auto query on data store with alias ${alias}.`,
      itemId,
    });
  }

  return [alerts, autoFixes];
}
